import { tracker } from '@lessonup/analytics';
import { trackLessonActionWithOrigin } from '@lessonup/client-integration';
import { logger } from '@lessonup/logging-classic';
import { EmbedErrorPage, EmbedErrorPageProps } from '@lessonup/teacher-modern';
import { EduSystem, formatSchoolType, Lesson } from '@lessonup/teaching-core';
import { ChannelTitle, IndexedChannelInfo } from '@lessonup/typescript-core/domain';
import { channelRoute, lessonRoute, editorV2Url } from '@lessonup/typescript-core/services';
import { useToasts, useCallbackIfParamsChanged } from '@lessonup/typescript-core/ui';
import { transformServingImage } from '@lessonup/typescript-core/utils';
import { AppError, naiveStripHTML } from '@lessonup/utils';
import { compact, intersection } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useAppServices } from '../../../components/appServices/AppServicesContext';
import {
  fetchedLessonAction,
  fetchingLessonAction,
  fetchLessonErrorAction,
  saveLessonAction,
} from '../../../redux/actions/actions';
import { isChannelFollowed, lessonStore, loggedInUser } from '../../../redux/selectors';
import { useTopLevelClassnameContext } from '../../../utils/TopLevelClassnameContext';
import { useSpinnerContext } from '../../../utils/loaders/SpinnerContext';
import pageTitle from '../../../utils/pageTitle';
import { useScrollToTop } from '../../../utils/useScrollToTop';
import ErrorPageView from '../../error';
import FlagLessonButton from '../flagLessonButton/FlagLessonButton';
import LessonPageViewV2 from './LessonPageV2View';

type Props = {
  lessonId: string;
  preselectedPin?: string;
  handlePinSelected: (pinId: string) => void;
  backUrl: string;
  backNavigation?: (lesson: Lesson) => React.JSX.Element | undefined;
  teachOverride?: (newTab?: boolean) => void;
  withoutHasMore?: boolean;
  embedLesson?: boolean;
};

const LessonPageV2: React.FC<Props> = ({
  lessonId,
  preselectedPin,
  handlePinSelected,
  backNavigation,
  backUrl,
  teachOverride,
  withoutHasMore,
  embedLesson,
}) => {
  const { lessonDetails, error, lessonId: storeLessonId, errorDetails } = useSelector(lessonStore());
  const channel = lessonDetails && lessonDetails.channel;
  const user = useSelector(loggedInUser());
  const services = useAppServices();
  const { user: userService, lesson: lessonService } = services;
  const channelFollowed = useSelector(isChannelFollowed(channel && channel.name));
  const [lessonSaved, setLessonSaved] = useState(false);
  const spinner = useSpinnerContext();
  const dispatch = useDispatch();
  const { t } = useTranslation('lessonDetails');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- auto-generated TECH-258
  const toastManager = useToasts();
  const { setClassName } = useTopLevelClassnameContext();
  useScrollToTop();

  useEffect(() => {
    setClassName('lesson-page-rendered');
    return () => {
      setClassName('');
    };
  });

  useEffect(() => {
    setLessonSaved(lessonService.isLessonSaved(lessonId));
  }, [lessonService, lessonId]);

  useCallbackIfParamsChanged({
    initial: storeLessonId,
    params: lessonId,
    async callback(lessonToFetch) {
      if (!lessonToFetch) return;
      spinner.open();
      dispatch(fetchingLessonAction(lessonToFetch));
      try {
        const result = await lessonService.lessonById(lessonToFetch, withoutHasMore);
        if (result) dispatch(fetchedLessonAction(result));
      } catch (error) {
        if (AppError.isError(error, 'not-allowed')) {
          dispatch(fetchLessonErrorAction(403, lessonToFetch));
        } else if (AppError.isError(error, 'not-allowed/no-product')) {
          dispatch(fetchLessonErrorAction(403, lessonToFetch, true));
        } else if (AppError.isError(error, 'not-found')) {
          dispatch(fetchLessonErrorAction(404, lessonToFetch));
        } else {
          dispatch(fetchLessonErrorAction(500, lessonToFetch));
        }
      } finally {
        spinner.close();
      }
    },
  });

  const handleLessonSave = async (lesson: Lesson, cancelOnLoginDismiss?: boolean) => {
    try {
      if (!userService.currentUser()) {
        const loginResponse = await services.modals.open('loginPrompt', { forceNewTab: !!embedLesson, action: 'save' });
        if (cancelOnLoginDismiss && loginResponse.dismiss) return;
      }
      const res = await lessonService.saveLesson(lesson._id);
      if (!res) return;
      tracker.events.lessonSaveForLater({ lessonId: lesson._id });
      trackLessonActionWithOrigin(
        'save-lesson',
        {
          lessonId: lesson._id,
          label: res.currentUser ? 'user' : 'non-user',
          channel: lesson.channel,
        },
        lesson
      );
      if (!res.currentUser) {
        setLessonSaved(true);
        return;
      }
      dispatch(saveLessonAction(lesson._id));
      services.modals.open('link', {
        title: t('lessonSavedTitle') || '',
        description: t('lessonSavedDescription'),
        link: editorV2Url(res.lessonId),
        linkText: t('openSavedLesson'),
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const navigateToLesson = (newTab?: boolean) => {
    if (teachOverride) {
      return teachOverride(newTab);
    }
    lessonService.teach({
      lessonId,
      newTab,
      pinId: preselectedPin,
      source: embedLesson ? 'searchembed' : 'search',
      version: 'v2',
    });
  };

  const shareLesson = () => {
    tracker.events.lessonShare({ lessonId, editorVersion: 'v2' });
    trackLessonActionWithOrigin('share-lesson', { lessonId, channel: lesson.channel }, lesson);
    const userHasAccessToProduct =
      intersection(
        user?.products.map((product) => product.productId),
        lesson.products
      ).length > 0;
    services.modals.open('shareLesson', {
      url: services.config.shareLessonUrl(lessonId),
      isSignedIn: Boolean(userService.currentUser()),
      hasProFeatures: userHasAccessToProduct || services.user.hasProFeatures(),
      canShareWithStudents: false,

      shareWithStudents: () => {},
    });
  };

  const isLessonSaved = () => {
    return userService.currentUser() ? lessonService.isLessonSaved(lessonId) : lessonSaved || false;
  };

  if (error && errorDetails?.forProduct) {
    if (error === 403 && embedLesson) {
      const channelTitle = lessonDetails?.channel?.title
        ? ChannelTitle.localizeTitle(lessonDetails?.channel?.title)
        : undefined;
      const channel: EmbedErrorPageProps['channel'] = lessonDetails?.channel &&
        channelTitle && {
          title: channelTitle,
          slug: lessonDetails.channel.name,
        };
      return <EmbedErrorPage channel={channel} style={{ height: '100%' }} />;
    }

    return <ErrorPageView type="lesson" backUrl={backUrl} code={error} voucher={true} errorDetails={errorDetails} />;
  }
  if (error) {
    return (
      <ErrorPageView
        type="lesson"
        backUrl={backUrl}
        code={error}
        backToLesson={lessonRoute(lessonId)}
        errorDetails={errorDetails}
      />
    );
  }

  if (!lessonDetails || storeLessonId !== lessonId || lessonDetails.version !== 'v2') return null;
  const { teacherPins, lesson, pinThemes } = lessonDetails;

  const flagLessonButton = <FlagLessonButton lessonId={lesson._id} />;

  // only show the button if we have a active lesson
  const activeLesson = user?.activeLesson;
  const activeDocument = user?.activeDocument;
  const addPin = activeLesson
    ? (pinId: string) => {
        return lessonService.addPinToActiveLesson(pinId);
      }
    : undefined;

  return (
    <div>
      <LessonPageMetaTags lesson={lesson} />
      <LessonPageViewV2
        className="lesson-page-view"
        pins={teacherPins || []}
        pinThemes={pinThemes}
        lesson={lesson}
        channel={channel}
        accessTypes={lessonDetails.accessTypes}
        onLessonSave={handleLessonSave}
        onPinSelected={handlePinSelected}
        preselectedPin={preselectedPin}
        navigateToLesson={navigateToLesson}
        shareLesson={shareLesson}
        isLessonSaved={isLessonSaved()}
        backNavigation={backNavigation && backNavigation(lesson)}
        flagLessonButton={flagLessonButton}
        channelUrl={(channelSlug: string) => channelRoute({ channelSlug })}
        channelFollowed={channelFollowed}
        userIsLoggedIn={!!user}
        addPin={addPin}
        activeLesson={activeLesson}
        activeDocument={activeDocument}
        embedLesson={embedLesson}
        currentLicense={user?.licenseStatus}
        eligibleToTeachWithoutCredits={lessonDetails.eligibleToTeachWithoutCredits}
      />

      <LessonAnalytics lesson={lesson} channel={channel}></LessonAnalytics>
    </div>
  );
};

function LessonPageMetaTags({ lesson }: { lesson: Lesson }) {
  const isChannelLesson = !!lesson.channel;
  const isUnlistedLesson = lesson.privacy === 'protected';
  const THUMB_WIDTH = 580;
  const thumbHeight = (THUMB_WIDTH / 16) * 9;
  return (
    <Helmet>
      {lesson.name && <title>{pageTitle(lesson.name)}</title>}
      <meta name="robots" content={isChannelLesson && !isUnlistedLesson ? 'index, noimageindex' : 'noindex'} />
      <meta property="twitter:card" content="summary" />
      <meta property="og:title" content={lesson.name} />
      <meta
        property="og:description"
        content={lesson.description ? naiveStripHTML(lesson.description) : generateDutchLessonDescription(lesson)}
      />
      {lesson.thumbnail && (
        <meta
          property="og:image"
          content={transformServingImage(lesson.thumbnail.url, { width: THUMB_WIDTH, height: thumbHeight })}
        />
      )}
    </Helmet>
  );
}

function generateDutchLessonDescription(lesson: Lesson) {
  const eduSystem = EduSystem.get('nl');
  const subjects = lesson.subjects?.slice(0, 2) || [];
  const duration = lesson.durationInMin;
  const schoolTypes = lesson.schoolType ? lesson.schoolType.slice(0, 2).map((t) => formatSchoolType(eduSystem, t)) : [];
  const elements = [
    'Les',
    subjects.join(' en '),
    duration && `van ${duration} minuten`,
    schoolTypes.length > 0 && `voor ${schoolTypes.join(' en ')}`,
  ];

  return compact(elements).join(' ');
}

export default LessonPageV2;

function LessonAnalytics({ lesson, channel }: { lesson: Lesson; channel?: IndexedChannelInfo }) {
  useEffect(() => {
    // new tracker is using the channel.id
    tracker.events.lessonView({ lessonId: lesson._id, channelId: channel?._id });
    // the old tracked used the channel.name
    trackLessonActionWithOrigin('view-lesson', { lessonId: lesson._id, channel: channel?.name }, lesson);
  }, [channel, lesson]);

  return null;
}
